import axios from 'axios';
import i18n from '../i18n';

const STORAGE_KEY = 'farm_smartly_session';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8081/api/farm-smartly/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT, 10) || 30000, // ms — prevents hung requests
});

// ── Request Deduplication ───────────────────────────────────────────────────
// Map to track in-flight requests and prevent duplicate simultaneous calls
// Entries: requestKey -> { promise, timestamp }
const pendingRequests = new Map();

// Safety net: if a tab is backgrounded (e.g. mobile PWA left open overnight),
// the browser/OS can silently drop an in-flight request without ever firing
// axios's success or error handlers. That leaves a permanently-unresolved
// entry in pendingRequests, and every later request for the same URL dedupes
// onto that dead promise and hangs forever until the page is reloaded.
// A TTL ensures a stale entry is discarded and a fresh request is issued
// instead of reusing it indefinitely.
const DEDUPE_TTL_MS = 30_000; // matches the default request timeout below

/**
 * Generate a unique key for a request based on method, URL, and params/data
 */
function getRequestKey(config) {
  const { method, url, params, data } = config;
  const keyParts = [method?.toLowerCase(), url];

  if (params) {
    keyParts.push(JSON.stringify(params));
  }
  if (data && typeof data === 'string') {
    keyParts.push(data);
  } else if (data) {
    keyParts.push(JSON.stringify(data));
  }

  return keyParts.join('|');
}

// ── Request interceptor — attach JWT & Tenant ID + deduplication ────────────
apiClient.interceptors.request.use((config) => {
  // Request deduplication for GET requests
  if (config.method?.toLowerCase() === 'get' && !config.skipDedupe) {
    const requestKey = getRequestKey(config);
    const pending = pendingRequests.get(requestKey);
    const isFresh = pending && (Date.now() - pending.timestamp) < DEDUPE_TTL_MS;

    if (isFresh) {
      // Return the pending promise for duplicate requests
      config.adapter = () => pending.promise;
    } else {
      if (pending) pendingRequests.delete(requestKey); // stale — drop it, issue a fresh request
      // Track this request
      const promise = new Promise((resolve, reject) => {
        config.__resolvePromise = resolve;
        config.__rejectPromise = reject;
      });
      pendingRequests.set(requestKey, { promise, timestamp: Date.now() });
      config.__requestKey = requestKey;
    }
  }

  // Attach auth headers
  try {
    const session = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (session?.token) {
      config.headers.Authorization = `Bearer ${session.token}`;
    }
    if (session?.tenantId) {
      config.headers['X-Tenant-Id'] = session.tenantId;
    }
  } catch { /* corrupt storage — proceed without token */ }
  return config;
});

// ── Response interceptor — normalise errors + cleanup deduplication ─────────
apiClient.interceptors.response.use(
  (response) => {
    // Cleanup pending request tracking
    const requestKey = response.config.__requestKey;
    if (requestKey) {
      pendingRequests.delete(requestKey);
      if (response.config.__resolvePromise) {
        response.config.__resolvePromise(response);
      }
    }
    return response;
  },
  (error) => {
    // Cleanup pending request tracking on error
    const requestKey = error.config?.__requestKey;
    if (requestKey) {
      pendingRequests.delete(requestKey);
      if (error.config?.__rejectPromise) {
        error.config.__rejectPromise(error);
      }
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const body = error.response.data;
      const message = typeof body?.message === 'string' ? body.message : '';
      // Only a rejected/missing/expired token should end the session. Business 401s
      // (wrong current password, failed biometric check, bad login) carry an ApiResponse
      // body with their own message; the security entry point's 401 has no such body.
      const isAuthEndpoint = url.includes('/auth/');
      const isTokenFailure =
        body?.success === undefined ||
        ['Unauthorized', 'Token has expired', 'Token is invalid'].includes(message);
      console.warn('[auth] 401 from', error.config?.method, url, '-', message || '(no body)',
        isTokenFailure && !isAuthEndpoint ? '→ clearing session' : '→ session kept');
      if (isAuthEndpoint || !isTokenFailure) {
        return Promise.reject(error);
      }
      localStorage.removeItem(STORAGE_KEY);
      // Don't redirect if already on login page to avoid refresh loop
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    // 403 is deliberately NOT a global redirect: a single role-restricted call
    // (e.g. a laborer hitting an OWNER/ADMIN-only endpoint) must not bounce the whole
    // session to /forbidden, which reads as a logout. Route-level access is enforced by
    // FinancialRoute/ModuleRoute; callers surface the 403 via getErrorMessage()/toasts.
    return Promise.reject(error);
  }
);

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Unwrap the ApiResponse envelope: { success, data, message, errors } → data */
export function unwrap(response) {
  return response.data?.data ?? response.data;
}

/** Extract a user-friendly error message from any Axios error. */
export function getErrorMessage(error) {
  // Axios timeout
  if (error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';

  // No response (network down, server unreachable)
  if (!error.response) return 'Unable to reach the server. Check your connection.';

  const body = error.response.data;

  // Validation errors — collapse field messages into one string
  if (body?.errors && typeof body.errors === 'object') {
    const fieldMessages = Object.values(body.errors).filter(Boolean);
    if (fieldMessages.length > 0) return fieldMessages.join(' · ');
  }

  // A promo code that went bad: the server sends a stable code (PROMO_EXPIRED...) so we can translate it
  if (typeof body?.errorCode === 'string' && body.errorCode.startsWith('PROMO_')) {
    const key = `promo.errors.${body.errorCode.slice(6)}`;
    if (i18n.exists(key)) return i18n.t(key, { ...(body.errorParams || {}), defaultValue: body.message });
  }

  // Standard message from ApiResponse
  if (typeof body?.message === 'string' && body.message) return body.message;

  // HTTP status fallbacks
  const status = error.response.status;
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 404) return 'The requested resource was not found.';
  if (status >= 500) return 'A server error occurred. Please try again later.';

  return 'Something went wrong. Please try again.';
}

export default apiClient;
